import React, { useState, useRef, forwardRef } from 'react';
import './FileDropZone.css';

const FileDropZone = forwardRef(({ 
  onFileDrop, 
  children, 
  className = '',
  acceptedTypes = null // e.g., ['.csv', '.xlsx', '.txt']
}, ref) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev - 1);
    
    if (dragCounter <= 1) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragOver(false);
    setDragCounter(0);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0]; // Take only the first file
      
      // Check file type if acceptedTypes is specified
      if (acceptedTypes && acceptedTypes.length > 0) {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        if (!acceptedTypes.includes(fileExtension)) {
          alert(`File type not supported. Accepted types: ${acceptedTypes.join(', ')}`);
          return;
        }
      }
      
      onFileDrop(file);
    }
  };

  const handleBrowseFiles = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const file = files[0];
      
      // Check file type if acceptedTypes is specified
      if (acceptedTypes && acceptedTypes.length > 0) {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        if (!acceptedTypes.includes(fileExtension)) {
          alert(`File type not supported. Accepted types: ${acceptedTypes.join(', ')}`);
          return;
        }
      }
      
      onFileDrop(file);
    }
    
    // Reset the file input
    e.target.value = '';
  };

  return (
    <div
      ref={ref}
      className={`file-drop-zone ${className} ${isDragOver ? 'drag-over' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes ? acceptedTypes.join(',') : undefined}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      
      {children}
      
      {/* Drop overlay when dragging */}
      {isDragOver && (
        <div className="drop-overlay">
          <div className="drop-message">
            <div className="drop-icon">📄</div>
            <h3>Drop file here</h3>
            <p>Release to process and extract data</p>
            {acceptedTypes && acceptedTypes.length > 0 && (
              <div className="supported-formats">
                <small>Supports: {acceptedTypes.slice(0, 6).join(', ')}{acceptedTypes.length > 6 ? ' + more' : ''}</small>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

FileDropZone.displayName = 'FileDropZone';

export default FileDropZone; 